import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { apiBase, authHeaders } from '../utils/authHeaders';
import { formatMoney } from '../utils/money';
import { exceptionLink, queueSummary, rankSites } from '../groupView';

// The owner's home: every site side by side, and what is waiting on a person. It reads two existing
// endpoints (/api/locations/rollup/summary and /api/owner/queue) and adds no logic of its own.

const PERIODS = [7, 30];
// The rollup carries exact decimals; a dashboard shows pence.
const pence = (v) => formatMoney(Number.isFinite(Number(v)) ? Number(v).toFixed(2) : v);

export default function GroupView() {
  const [days, setDays] = useState(7);
  const [rollup, setRollup] = useState(null);
  const [queue, setQueue] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);

  const load = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const headers = authHeaders();
      const [r, q] = await Promise.all([
        fetch(`${apiBase()}/api/locations/rollup/summary?period_days=${days}`, { headers }),
        fetch(`${apiBase()}/api/owner/queue`, { headers }),
      ]);
      if (!alive.current) return;
      if (r.status === 403) { setError('Your role cannot view the group.'); return; }
      if (!r.ok) throw new Error(`Group request failed (${r.status})`);
      setRollup(await r.json());
      setQueue(q.ok ? await q.json() : null);
    } catch (e) {
      if (alive.current) setError(e.message || 'Could not load the group');
    } finally {
      if (alive.current) setBusy(false);
    }
  }, [days]);

  useEffect(() => {
    alive.current = true;
    load();
    return () => { alive.current = false; };
  }, [load]);

  const sites = rankSites(rollup);
  const work = queueSummary(queue);

  return (
    <div className="p-6" data-testid="group-view">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Group view</h1>
          <p className="text-gray-600">Every site, and what is waiting on a person.</p>
        </div>
        <div className="flex items-center gap-2">
          <select aria-label="Period" value={days} onChange={(e) => setDays(Number(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-lg">
            {PERIODS.map((d) => <option key={d} value={d}>Last {d} days</option>)}
          </select>
          <button onClick={load} disabled={busy} aria-label="Refresh"><RefreshCw size={18} /></button>
        </div>
      </div>

      {error && <p role="alert" className="mb-4 text-red-700">{error}</p>}

      {rollup && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="group-totals">
          <Stat label="Revenue" value={pence(rollup.total_revenue)} />
          <Stat label="Orders" value={Number(rollup.order_count).toLocaleString('en-GB')} />
          <Stat label="Stock on hand" value={pence(rollup.stock_value)} />
          <Stat label="Open alerts" value={rollup.open_alerts} />
        </div>
      )}

      <h2 className="text-xl font-semibold mb-2">Sites</h2>
      <div className="grid md:grid-cols-2 gap-4 mb-8" data-testid="group-sites">
        {sites.map((s) => (
          <div key={s.id} className="border rounded-lg p-4 bg-white" data-testid={`group-site-${s.id}`}>
            <div className="flex justify-between">
              <strong>{s.name}</strong>
              {s.alerts > 0 && <span className="text-amber-700">{s.alerts} open alert{s.alerts > 1 ? 's' : ''}</span>}
            </div>
            {s.sells ? (
              <p className="text-sm text-gray-700 mt-1">
                {pence(s.revenue)} from {s.orders.toLocaleString('en-GB')} orders
                ({pence(s.aov)} each) · {s.share}% of the group
              </p>
            ) : (
              <p className="text-sm text-gray-500 mt-1">Production site: no till sales.</p>
            )}
            <p className="text-sm text-gray-700">
              Stock {pence(s.stockValue)} across {s.stockItems} items
            </p>
          </div>
        ))}
        {rollup && sites.length === 0 && <p>No sites yet.</p>}
      </div>

      <h2 className="text-xl font-semibold mb-2">Waiting on a person</h2>
      <section aria-label="Work queue" data-testid="group-queue">
        {!queue && <p className="text-gray-500">The work queue could not be read.</p>}
        {queue && !work.known && (
          <p role="alert" className="text-amber-800">
            <AlertTriangle size={16} className="inline" /> Some sources could not be read, so no count is shown
            (unread: {work.unread.join(', ')}).
          </p>
        )}
        {work.known && work.count === 0 && <p className="text-gray-600">Nothing is waiting. Verify against your sources before closing the day.</p>}
        {work.items.length > 0 && (
          <ul className="space-y-2">
            {work.items.map((e) => (
              <li key={`${e.source}:${e.ref}`} className="border rounded p-3 bg-white" data-severity={e.severity}>
                <Link to={exceptionLink(e)} className="font-medium text-blue-700">{e.title}</Link>
                <span className="ml-2 text-sm text-gray-500">{String(e.kind).replace(/_/g, ' ')}</span>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-3"><Link to="/owner/queue" className="text-blue-700">Open the full work queue</Link></p>
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border rounded-lg p-4 bg-white">
      <div className="text-sm text-gray-500">{label}</div>
      <div className="text-2xl font-semibold">{value}</div>
    </div>
  );
}
